import { c as _c } from "react/compiler-runtime";
import { Button } from "@repo/ui/components/ui/button";
import { Input } from "@repo/ui/components/ui/input";
import { Label } from "@repo/ui/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/ui/components/ui/select";
import { Textarea } from "@repo/ui/components/ui/textarea";
import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { trpcClient } from "@/clients/trpcClient";
import { useIsAdmin } from "@/lib/hooks/useIsAdmin";
import { parseRouteId } from "@/lib/utils/routeId";
import { trpc } from "@/router";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function PlaceEditorView() {
  const $ = _c(77);
  const isAdmin = useIsAdmin();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      from: "/_app/admin/platser/$id"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    id
  } = useParams(t0);
  const placeId = parseRouteId(id);
  const queryClient = useQueryClient();
  const {
    data: place,
    isLoading
  } = useQuery(trpc.places.byId.queryOptions(placeId ? {
    id: placeId
  } : skipToken));
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = trpc.experiences.categories.all.queryOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: t2
  } = useQuery(t1);
  let t3;
  if ($[2] !== t2) {
    t3 = t2 === undefined ? [] : t2;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const allCategories = t3;
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("none");
  let t4;
  let t5;
  if ($[4] !== allCategories || $[5] !== place) {
    t4 = () => {
      if (place) {
        setName(place.name);
        setDescription(place.description ?? "");
        setCategoryId(place.categoryIcon ? String(allCategories.find(c => c.name === place.categoryName)?.id ?? "none") : "none");
      }
    };
    t5 = [place, allCategories];
    $[4] = allCategories;
    $[5] = place;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== categoryId || $[9] !== description || $[10] !== name || $[11] !== placeId) {
    t6 = () => {
      if (placeId === null) {
        throw new Error("Invalid place id");
      }
      return trpcClient.places.update.mutate({
        id: placeId,
        name: name.trim(),
        description: description.trim() || null,
        categoryId: categoryId === "none" ? null : Number(categoryId)
      });
    };
    $[8] = categoryId;
    $[9] = description;
    $[10] = name;
    $[11] = placeId;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== queryClient) {
    t7 = () => {
      toast.success("Plats uppdaterad");
      queryClient.invalidateQueries({
        queryKey: [["places"]]
      });
    };
    $[13] = queryClient;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = {
      mutationFn: t6,
      onSuccess: t7,
      onError: _temp
    };
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const updateMutation = useMutation(t8);
  if (!isAdmin) {
    let t9;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-gray-500",
          children: "Du har inte beh\xF6righet."
        })
      });
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    return t9;
  }
  if (isLoading) {
    let t9;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx(Loader2, {
          className: "h-6 w-6 animate-spin text-gray-400"
        })
      });
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    return t9;
  }
  if (!place) {
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = /*#__PURE__*/_jsx("div", {
        className: "container mx-auto flex min-h-[60vh] items-center justify-center px-4",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-gray-500",
          children: "Platsen hittades inte."
        })
      });
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    return t9;
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = /*#__PURE__*/_jsxs("div", {
      className: "flex items-center gap-3",
      children: [/*#__PURE__*/_jsxs(Button, {
        variant: "ghost",
        size: "sm",
        onClick: _temp2,
        children: [/*#__PURE__*/_jsx(ArrowLeft, {
          className: "mr-1 h-4 w-4"
        }), "Tillbaka"]
      }), /*#__PURE__*/_jsx("h1", {
        className: "text-xl font-bold text-gray-900",
        children: "Redigera plats"
      })]
    });
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== updateMutation) {
    t10 = () => updateMutation.mutate();
    $[22] = updateMutation;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== name || $[25] !== updateMutation.isPending) {
    t11 = updateMutation.isPending || !name.trim();
    $[24] = name;
    $[25] = updateMutation.isPending;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== updateMutation.isPending) {
    t12 = updateMutation.isPending ? /*#__PURE__*/_jsx(Loader2, {
      className: "h-4 w-4 animate-spin"
    }) : /*#__PURE__*/_jsx(Save, {
      className: "h-4 w-4"
    });
    $[27] = updateMutation.isPending;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t11 || $[31] !== t12) {
    t13 = /*#__PURE__*/_jsxs("div", {
      className: "mb-6 flex items-center justify-between",
      children: [t9, /*#__PURE__*/_jsxs(Button, {
        onClick: t10,
        disabled: t11,
        className: "gap-1.5 bg-brand hover:bg-brand/90",
        size: "sm",
        children: [t12, "Spara"]
      })]
    });
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = /*#__PURE__*/_jsx(Label, {
      children: "Namn"
    });
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => setName(e.target.value);
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== name) {
    t16 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-1.5",
      children: [t14, /*#__PURE__*/_jsx(Input, {
        value: name,
        onChange: t15
      })]
    });
    $[35] = name;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = /*#__PURE__*/_jsx(Label, {
      children: "Kategori"
    });
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = /*#__PURE__*/_jsx(SelectTrigger, {
      children: /*#__PURE__*/_jsx(SelectValue, {
        placeholder: "V\xE4lj kategori"
      })
    });
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = /*#__PURE__*/_jsx(SelectItem, {
      value: "none",
      children: "Ingen kategori"
    });
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== allCategories) {
    t20 = allCategories.map(_temp3);
    $[40] = allCategories;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = /*#__PURE__*/_jsxs(SelectContent, {
      children: [t19, t20]
    });
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== categoryId || $[45] !== t21) {
    t22 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-1.5",
      children: [t17, /*#__PURE__*/_jsxs(Select, {
        value: categoryId,
        onValueChange: setCategoryId,
        children: [t18, t21]
      })]
    });
    $[44] = categoryId;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = /*#__PURE__*/_jsx(Label, {
      children: "Beskrivning"
    });
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_0 => setDescription(e_0.target.value);
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== description) {
    t25 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-1.5",
      children: [t23, /*#__PURE__*/_jsx(Textarea, {
        value: description,
        onChange: t24,
        placeholder: "Beskrivning av platsen och dess tillg\xE4nglighet...",
        className: "h-32 resize-none"
      })]
    });
    $[49] = description;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== place.id) {
    t26 = /*#__PURE__*/_jsxs("p", {
      children: ["ID: ", place.id]
    });
    $[51] = place.id;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] !== place.location.latitude) {
    t27 = place.location.latitude.toFixed(5);
    $[53] = place.location.latitude;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== place.location.longitude) {
    t28 = place.location.longitude.toFixed(5);
    $[55] = place.location.longitude;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  let t29;
  if ($[57] !== t27 || $[58] !== t28) {
    t29 = /*#__PURE__*/_jsxs("p", {
      children: ["Position: ", t27, ", ", t28]
    });
    $[57] = t27;
    $[58] = t28;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  let t30;
  if ($[60] !== place.osmId) {
    t30 = place.osmId && /*#__PURE__*/_jsxs("p", {
      children: ["OSM ID: ", place.osmId]
    });
    $[60] = place.osmId;
    $[61] = t30;
  } else {
    t30 = $[61];
  }
  let t31;
  if ($[62] !== place.id) {
    t31 = /*#__PURE__*/_jsx(Link, {
      to: "/karta",
      search: {
        plats: place.id
      },
      className: "mt-1 inline-block text-brand hover:underline",
      children: "Visa p\xE5 kartan \u2192"
    });
    $[62] = place.id;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t26 || $[65] !== t29 || $[66] !== t30 || $[67] !== t31) {
    t32 = /*#__PURE__*/_jsxs("div", {
      className: "rounded-lg border bg-gray-50 p-3 text-xs text-gray-500",
      children: [t26, t29, t30, t31]
    });
    $[64] = t26;
    $[65] = t29;
    $[66] = t30;
    $[67] = t31;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t16 || $[70] !== t22 || $[71] !== t25 || $[72] !== t32) {
    t33 = /*#__PURE__*/_jsxs("div", {
      className: "space-y-5",
      children: [t16, t22, t25, t32]
    });
    $[69] = t16;
    $[70] = t22;
    $[71] = t25;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t13 || $[75] !== t33) {
    t34 = /*#__PURE__*/_jsxs("div", {
      className: "container mx-auto max-w-2xl px-4 py-8",
      children: [t13, t33]
    });
    $[74] = t13;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  return t34;
}
function _temp3(cat) {
  return /*#__PURE__*/_jsx(SelectItem, {
    value: String(cat.id),
    children: cat.name
  }, cat.id);
}
function _temp2() {
  return window.history.back();
}
function _temp(err) {
  return toast.error("Kunde inte uppdatera", {
    description: err instanceof Error ? err.message : undefined
  });
}